import { Injectable, NgZone, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { combineLatest, firstValueFrom } from 'rxjs';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';
import { environment } from '@environments/environment';
import { PENDO_AGENT_CDN_BASE, PENDO_SCRIPT_ID } from 'src/app/core/constants/pendo.constants';
import { CookieConsentService } from '../cookie-consent/cookie-consent.service';
import { MarketService } from '../market.service';
import { UserService } from '../user/user.service';
import { buildPendoVisitorMetadata } from './pendo-visitor.util';
import type { PendoAgent, PendoInitializeOptions, PendoWindow } from './pendo.types';

/**
 * Loads and drives Pendo analytics for authenticated users who have granted analytics consent.
 *
 * Tracking is gated on BOTH conditions (login + consent) via a single combined stream, so it
 * starts when both become true and tears down when either becomes false. Only the agreed
 * metadata (user token, age band, city, state, country, postal, party type, environment) is sent.
 * EDS integration is deferred to a future phase and is intentionally not wired here.
 */
@Injectable({ providedIn: 'root' })
export class PendoAnalyticsService {
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  private readonly userService = inject(UserService);
  private readonly marketService = inject(MarketService);
  private readonly cookieConsent = inject(CookieConsentService);
  private readonly ngZone = inject(NgZone);

  private scriptLoadPromise: Promise<void> | null = null;
  private initialized = false;
  private initInFlight = false;

  constructor() {
    this.bindTrackingLifecycle();
    this.bindRouteChanges();
  }

  /**
   * Explicitly re-checks consent/login state and starts Pendo immediately when both are already
   * satisfied. This is used by SPA consent flows where third-party callbacks can lag and we do not
   * want to wait for a page refresh to re-enter the bootstrap path.
   */
  public ensureTrackingInitialized(): void {
    if (!this.cookieConsent.hasAnalyticsConsent()) {
      return;
    }

    void this.initializeTracking();
  }

  /**
   * Sends a safe custom event to Pendo. Business events are intentionally limited to anonymised
   * metadata and never include raw customer content or identifiers.
   */
  public track(eventName: string, properties?: Record<string, unknown>): void {
    const agent = this.getAgent();
    if (!agent?.track || !eventName) {
      return;
    }

    const safeProperties = this.sanitizeTrackingProperties(properties);
    const payload = {
      ...(safeProperties ?? {}),
      ...(environment.pendo?.environment ? { environment: environment.pendo.environment } : {}),
    };

    agent.track(eventName, payload);
  }

  /**
   * Stops Pendo tracking and removes the agent. Called on logout (before storage is cleared) and
   * whenever consent is revoked. Safe to invoke when Pendo was never initialized.
   */
  teardown(): void {
    this.initialized = false;
    this.initInFlight = false;
    this.scriptLoadPromise = null;

    const agent = this.getAgent();
    if (agent?.teardown) {
      try {
        agent.teardown();
      } catch {
        // Non-fatal: logout must not fail because analytics teardown failed.
      }
    }

    document.getElementById(PENDO_SCRIPT_ID)?.remove();
    delete (globalThis.window as PendoWindow).pendo;
  }

  /**
   * Tracking runs only while the user is authenticated AND has granted analytics consent.
   * Combining both sources keeps a single source of truth: grant/login starts tracking, and
   * logout/consent-revocation tears it down.
   */
  private bindTrackingLifecycle(): void {
    const hasConsent$ = this.cookieConsent.status$.pipe(
      map(() => this.cookieConsent.hasAnalyticsConsent()),
    );
    const isAuthenticated$ = this.auth.isAuthenticated$.pipe(
      filter((value): value is boolean => value !== undefined),
    );

    combineLatest([isAuthenticated$, hasConsent$])
      .pipe(
        map(([isAuthenticated, hasConsent]) => isAuthenticated && hasConsent),
        distinctUntilChanged(),
      )
      .subscribe((shouldTrack) => {
        if (shouldTrack) {
          void this.initializeTracking();
          return;
        }

        this.teardown();
      });
  }

  private bindRouteChanges(): void {
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(() => {
        if (!this.initialized) {
          return;
        }

        // Safe to call directly post-init; the agent handles SPA page transitions.
        this.getAgent()?.pageLoad();
      });
  }

  private async initializeTracking(): Promise<void> {
    if (this.initialized || this.initInFlight || !this.isPendoEnabled()) {
      return;
    }

    this.initInFlight = true;

    try {
      const userData = await firstValueFrom(this.userService.loadUserApiDataOnce$());
      if (!userData) {
        return;
      }

      const visitor = buildPendoVisitorMetadata(
        userData,
        this.marketService.getMarket(),
        environment.pendo?.environment,
      );
      if (!visitor) {
        // User token (UserKey) is required — never fall back to numeric ids or PII.
        return;
      }

      await this.loadAgentScript();
      this.initializeAgent({ visitor });
      this.initialized = true;
    } catch {
      // Analytics must never block or break the authenticated session.
    } finally {
      this.initInFlight = false;
    }
  }

  private isPendoEnabled(): boolean {
    const apiKey = (environment.pendo?.apiKey ?? '').trim();
    return Boolean(environment.pendo?.enabled && apiKey);
  }

  private sanitizeTrackingProperties(
    properties?: Record<string, unknown>,
  ): Record<string, unknown> | undefined {
    if (!properties) {
      return undefined;
    }

    const safeProperties: Record<string, unknown> = {};

    Object.entries(properties).forEach(([key, value]) => {
      if (value === null || value === undefined) {
        return;
      }

      if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed) {
          return;
        }

        safeProperties[key] = trimmed.slice(0, 200);
        return;
      }

      if (typeof value === 'number' || typeof value === 'boolean') {
        safeProperties[key] = value;
      }
    });

    return Object.keys(safeProperties).length > 0 ? safeProperties : undefined;
  }

  /**
   * Initializes the agent with our anonymised visitor. `pendo.initialize` MUST be called directly
   * (not gated on `isReady`/`events.ready`): those only resolve AFTER initialize, so gating here
   * would deadlock and let the agent launch anonymously (`_PENDO_T_...` visitor ids).
   */
  private initializeAgent(options: PendoInitializeOptions): void {
    const agent = this.getAgent();
    if (!agent) {
      return;
    }

    agent.initialize(options);
    // Record an initial page view once the agent has finished initializing.
    if (agent.isReady?.()) {
      agent.pageLoad();
      return;
    }

    agent.events?.ready(() => {
      this.ngZone.run(() => {
        this.getAgent()?.pageLoad();
      });
    });
  }

  private getAgent(): PendoAgent | undefined {
    return (globalThis.window as PendoWindow).pendo;
  }

  /**
   * Lazily injects the Pendo agent script after login + consent.
   * Deferred load keeps the login bundle small and avoids third-party work until needed.
   */
  private loadAgentScript(): Promise<void> {
    if (this.getAgent()) {
      return Promise.resolve();
    }

    if (this.scriptLoadPromise) {
      return this.scriptLoadPromise;
    }

    const apiKey = (environment.pendo?.apiKey ?? '').trim();
    this.scriptLoadPromise = new Promise<void>((resolve, reject) => {
      const existing = document.getElementById(PENDO_SCRIPT_ID);
      if (existing) {
        existing.addEventListener('load', () => resolve(), { once: true });
        existing.addEventListener('error', () => reject(new Error('Pendo script failed to load')), {
          once: true,
        });
        return;
      }

      const script = document.createElement('script');
      script.id = PENDO_SCRIPT_ID;
      script.async = true;
      script.defer = true;
      script.src = `${PENDO_AGENT_CDN_BASE}/${encodeURIComponent(apiKey)}/pendo.js`;

      script.onload = () => resolve();
      script.onerror = () => {
        this.scriptLoadPromise = null;
        reject(new Error('Pendo script failed to load'));
      };

      document.head.appendChild(script);
    });

    return this.scriptLoadPromise;
  }
}
